import { AlertCircle, BookOpen, LockKeyhole, RouteOff, ShieldAlert } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Skeleton } from "@/components/ui/skeleton"
import type { LearnerAccess } from "@/lib/learner-access"

export function PageLoading({ label }: { label?: string }) {
  const { t } = useTranslation()
  return (
    <div className="page-state-stack" role="status" aria-label={label ?? t("states.loading")}>
      <Skeleton className="h-28 rounded-lg" />
      <Skeleton className="h-40 rounded-lg" />
      <Skeleton className="h-28 rounded-lg" />
    </div>
  )
}

export function PageError({ retry }: { retry: () => void }) {
  const { t } = useTranslation()
  return (
    <Alert variant="destructive">
      <AlertCircle aria-hidden="true" />
      <AlertTitle>{t("states.errorTitle")}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-4">
        <p>{t("states.errorBody")}</p>
        <Button variant="outline" type="button" onClick={retry}>
          {t("retry")}
        </Button>
      </AlertDescription>
    </Alert>
  )
}

export function PageAccess({ access }: { access: Exclude<LearnerAccess, { status: "ready" }> }) {
  const { t } = useTranslation()
  const enrollment = access.status === "enrollment_required"
  const guardian = access.status === "guardian_required"
  const terms = access.status === "terms_acceptance_required"
  const restricted = access.status === "account_restricted"
  const Icon =
    enrollment || terms ? BookOpen : guardian ? LockKeyhole : restricted ? ShieldAlert : RouteOff
  const action =
    enrollment || terms ? "/onboarding" : guardian || restricted ? "/account" : undefined

  return (
    <Empty className="surface-card page-empty">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>{t(`access.${access.status}.title`)}</EmptyTitle>
        <EmptyDescription>{t(`access.${access.status}.body`)}</EmptyDescription>
      </EmptyHeader>
      {action ? (
        <EmptyContent>
          <Button asChild>
            <a href={action}>{t(`access.${access.status}.action`)}</a>
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  )
}

export function PageEmpty({
  action,
  actionLabel,
  description,
  title,
}: {
  action?: string
  actionLabel?: string
  description: string
  title: string
}) {
  return (
    <Empty className="surface-card page-empty">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <BookOpen aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {action && actionLabel ? (
        <EmptyContent>
          <Button asChild variant="secondary">
            <a href={action}>{actionLabel}</a>
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  )
}
